import React, { useState, useEffect } from 'react';
import { Download, Upload, Database, Settings, AlertCircle, Brain, Smartphone, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { userProfileService } from '../../../services/supabaseService';

const APP_VERSION = '2.1.0';

const AdvancedSettings = ({ profileData, onUpdate, isEditing }) => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [exportFormat, setExportFormat] = useState('json');
  const [importFile, setImportFile] = useState(null);
  const [exportStatus, setExportStatus] = useState(null); // 'loading' | 'success' | 'error'
  const [importStatus, setImportStatus] = useState(null); // 'loading' | 'success' | 'error' | 'preview'
  const [importPreview, setImportPreview] = useState(null);
  const [importMessage, setImportMessage] = useState('');
  const [systemInfo, setSystemInfo] = useState({
    appVersion: APP_VERSION,
    lastSync: 'Loading...',
    storageUsed: 'Loading...',
    accountSince: 'Loading...',
  });

  const [preferences, setPreferences] = useState({
    theme: theme || 'dark',
    language: profileData?.languagePreference || 'en',
  });

  // Load real system info
  useEffect(() => {
    const loadSystemInfo = async () => {
      if (!user?.id) return;
      try {
        const { data: profile } = await supabase
          ?.from('user_profiles')
          ?.select('created_at, last_activity_date')
          ?.eq('id', user?.id)
          ?.single();

        const { count: verseCount } = await supabase
          ?.from('verses')
          ?.select('id', { count: 'exact', head: true })
          ?.eq('user_id', user?.id);

        const { count: reflectionCount } = await supabase
          ?.from('verse_reflections')
          ?.select('id', { count: 'exact', head: true })
          ?.eq('user_id', user?.id);

        const accountSince = profile?.created_at
          ? new Date(profile.created_at)?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
          : 'Unknown';

        const lastActivity = profile?.last_activity_date
          ? new Date(profile.last_activity_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : 'No activity yet';

        // Rough estimate: ~500 bytes per verse, ~300 bytes per reflection
        const estimatedBytes = ((verseCount || 0) * 500) + ((reflectionCount || 0) * 300);
        const storageUsed = estimatedBytes < 1024 * 1024
          ? `${(estimatedBytes / 1024)?.toFixed(1)} KB`
          : `${(estimatedBytes / (1024 * 1024))?.toFixed(1)} MB`;

        setSystemInfo({
          appVersion: APP_VERSION,
          lastSync: lastActivity,
          storageUsed: storageUsed,
          accountSince,
        });
      } catch (err) {
        console.error('Error loading system info:', err);
      }
    };
    loadSystemInfo();
  }, [user?.id]);

  // Sync preferences with profileData when it changes
  useEffect(() => {
    setPreferences(prev => ({
      ...prev,
      theme: theme || 'dark',
      language: profileData?.languagePreference || prev?.language || 'en',
    }));
  }, [profileData?.languagePreference, theme]);

  const handleDataExport = async () => {
    if (!user?.id) return;
    setExportStatus('loading');
    try {
      const [versesRes, reflectionsRes, profileRes] = await Promise.all([
        supabase?.from('verses')?.select('*')?.eq('user_id', user?.id),
        supabase?.from('verse_reflections')?.select('*')?.eq('user_id', user?.id),
        supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single(),
      ]);

      const exportData = {
        exportDate: new Date()?.toISOString(),
        appVersion: APP_VERSION,
        profile: {
          email: profileRes?.data?.email,
          username: profileRes?.data?.username,
          firstName: profileRes?.data?.first_name,
          lastName: profileRes?.data?.last_name,
          timezone: profileRes?.data?.timezone,
          createdAt: profileRes?.data?.created_at,
        },
        verses: versesRes?.data || [],
        reflections: reflectionsRes?.data || [],
      };

      let content, mimeType, ext;
      if (exportFormat === 'json') {
        content = JSON.stringify(exportData, null, 2);
        mimeType = 'application/json';
        ext = 'json';
      } else if (exportFormat === 'csv') {
        // CSV: export verses as CSV
        const verses = exportData?.verses;
        if (verses?.length === 0) {
          content = 'reference,text,phase,created_at\n';
        } else {
          const headers = Object.keys(verses?.[0])?.join(',');
          const rows = verses?.map(v => Object.values(v)?.map(val => `"${String(val ?? '')?.replace(/"/g, '""')}"`)?.join(','));
          content = [headers, ...rows]?.join('\n');
        }
        mimeType = 'text/csv';
        ext = 'csv';
      } else {
        // XML
        const toXml = (obj, tag) => {
          if (Array.isArray(obj)) return obj?.map(item => toXml(item, 'item'))?.join('');
          if (typeof obj === 'object' && obj !== null) {
            return `<${tag}>${Object.entries(obj)?.map(([k, v]) => toXml(v, k))?.join('')}</${tag}>`;
          }
          return `<${tag}>${String(obj ?? '')?.replace(/&/g, '&amp;')?.replace(/</g, '&lt;')}</${tag}>`;
        };
        content = `<?xml version="1.0" encoding="UTF-8"?>${toXml(exportData, 'export')}`;
        mimeType = 'application/xml';
        ext = 'xml';
      }

      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fortifiedmind-export-${new Date()?.toISOString()?.split('T')?.[0]}.${ext}`;
      a?.click();
      URL.revokeObjectURL(url);
      setExportStatus('success');
      setTimeout(() => setExportStatus(null), 3000);
    } catch (err) {
      console.error('Export error:', err);
      setExportStatus('error');
      setTimeout(() => setExportStatus(null), 4000);
    }
  };

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setImportFile(file);
    setImportStatus(null);
    setImportPreview(null);
    setImportMessage('');

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const parsed = JSON.parse(evt?.target?.result);
        const verseCount = parsed?.verses?.length || 0;
        const reflectionCount = parsed?.reflections?.length || 0;
        setImportPreview({ verseCount, reflectionCount, exportDate: parsed?.exportDate });
        setImportStatus('preview');
      } catch {
        setImportStatus('error');
        setImportMessage('Invalid file format. Only JSON exports from Fortified Mind are supported.');
      }
    };
    reader?.readAsText(file);
  };

  const handleDataImport = async () => {
    if (!importFile || !user?.id || importStatus !== 'preview') return;
    setImportStatus('loading');
    setImportMessage('');
    try {
      const text = await importFile?.text();
      const parsed = JSON.parse(text);
      const verses = parsed?.verses || [];
      const reflections = parsed?.reflections || [];

      let importedVerses = 0;
      let importedReflections = 0;

      // Import verses in batches (strip id to avoid conflicts, reassign user_id)
      if (verses?.length > 0) {
        const versesToInsert = verses?.map(({ id, ...v }) => ({ ...v, user_id: user?.id }));
        const batchSize = 50;
        for (let i = 0; i < versesToInsert?.length; i += batchSize) {
          const batch = versesToInsert?.slice(i, i + batchSize);
          const { error } = await supabase?.from('verses')?.upsert(batch, { onConflict: 'user_id,reference' });
          if (!error) importedVerses += batch?.length;
        }
      }

      // Import reflections
      if (reflections?.length > 0) {
        const reflectionsToInsert = reflections?.map(({ id, ...r }) => ({ ...r, user_id: user?.id }));
        const batchSize = 50;
        for (let i = 0; i < reflectionsToInsert?.length; i += batchSize) {
          const batch = reflectionsToInsert?.slice(i, i + batchSize);
          const { error } = await supabase?.from('verse_reflections')?.upsert(batch, { onConflict: 'user_id,verse_reference' });
          if (!error) importedReflections += batch?.length;
        }
      }

      setImportStatus('success');
      setImportMessage(`Successfully imported ${importedVerses} verse(s) and ${importedReflections} reflection(s).`);
      setImportFile(null);
      setImportPreview(null);
    } catch (err) {
      console.error('Import error:', err);
      setImportStatus('error');
      setImportMessage('Import failed. Please ensure the file is a valid Fortified Mind export.');
    }
  };

  const handleMemorizationChange = (key, value) => {
    const newMemorization = { ...profileData?.memorization, [key]: value };
    if (isEditing) onUpdate?.({ memorization: newMemorization });
  };

  const handlePreferenceChange = async (key, value) => {
    const newPreferences = { ...preferences, [key]: value };
    setPreferences(newPreferences);

    if (key === 'theme') {
      // Apply theme immediately via ThemeContext
      if (value !== theme) toggleTheme();
    }

    if (isEditing && user?.id) {
      try {
        if (key === 'language') {
          await userProfileService?.updateUserProfile(user?.id, { languagePreference: value });
        } else if (key === 'theme') {
          // Store theme in notification_preferences JSONB
          const currentNotifPrefs = profileData?.notificationPreferences || {};
          await userProfileService?.updateUserProfile(user?.id, {
            notificationPreferences: { ...currentNotifPrefs, themePreference: value }
          });
        }
      } catch (err) {
        console.error('Error saving preference:', err);
      }
    }
  };

  const selectClass = (disabled) =>
    `block w-full px-3 py-2 border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm ${
      disabled ? 'bg-muted border-border cursor-not-allowed opacity-60' : 'bg-background border-border'
    }`;

  const inputClass = (disabled) =>
    `block w-full px-3 py-2 border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm ${
      disabled ? 'bg-muted border-border cursor-not-allowed opacity-60' : 'bg-background border-border'
    }`;

  return (
    <div className="space-y-6">
      {/* Memorization Configuration */}
      <div className="bg-card rounded-2xl border border-border shadow-warm-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
            <Brain className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-bold text-foreground">Memorization Configuration</h2>
            <p className="text-muted-foreground">Customize your verse memorization and review settings</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Phase 1 Duration (Days)</label>
            <div className="relative">
              <input type="number" min="1" max="90" value={profileData?.memorization?.phase1Days || 30}
                onChange={(e) => handleMemorizationChange('phase1Days', parseInt(e?.target?.value))}
                disabled={!isEditing} className={inputClass(!isEditing)} />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span className="text-muted-foreground text-sm">days</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Consecutive daily reviews to advance from Phase 1 to Phase 2 (default: 30)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Daily Review Period (Days)</label>
            <div className="relative">
              <input type="number" min="1" max="365" value={profileData?.memorization?.dailyReviewDays || 60}
                onChange={(e) => handleMemorizationChange('dailyReviewDays', parseInt(e?.target?.value))}
                disabled={!isEditing} className={inputClass(!isEditing)} />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span className="text-muted-foreground text-sm">days</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Legacy setting - kept for compatibility (default: 60 days)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Weekly Review Period (Weeks)</label>
            <div className="relative">
              <input type="number" min="1" max="52" value={profileData?.memorization?.weeklyReviewWeeks || 6}
                onChange={(e) => handleMemorizationChange('weeklyReviewWeeks', parseInt(e?.target?.value))}
                disabled={!isEditing} className={inputClass(!isEditing)} />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span className="text-muted-foreground text-sm">weeks</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">How long verses stay in weekly review (default: 6 weeks)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Monthly Review Period (Months)</label>
            <div className="relative">
              <input type="number" min="1" max="24" value={profileData?.memorization?.monthlyReviewMonths || 3}
                onChange={(e) => handleMemorizationChange('monthlyReviewMonths', parseInt(e?.target?.value))}
                disabled={!isEditing} className={inputClass(!isEditing)} />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span className="text-muted-foreground text-sm">months</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">How long verses stay in monthly review (default: 3 months)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">New Verse Addition Interval</label>
            <div className="flex space-x-2">
              <div className="flex-1">
                <input type="number" min="1" max="12" value={profileData?.memorization?.addToMemorizationIntervalValue || 1}
                  onChange={(e) => handleMemorizationChange('addToMemorizationIntervalValue', parseInt(e?.target?.value))}
                  disabled={!isEditing} className={inputClass(!isEditing)} />
              </div>
              <div className="flex-1">
                <select value={profileData?.memorization?.addToMemorizationInterval || 'weekly'}
                  onChange={(e) => handleMemorizationChange('addToMemorizationInterval', e?.target?.value)}
                  disabled={!isEditing} className={selectClass(!isEditing)}>
                  <option value="daily">Day(s)</option>
                  <option value="weekly">Week(s)</option>
                  <option value="monthly">Month(s)</option>
                </select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">How often to add verses from future verses bucket to memorization</p>
          </div>
        </div>

        <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <h4 className="font-medium text-foreground mb-2">Current Configuration Summary</h4>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>• Verses will be reviewed daily for <strong className="text-foreground">{profileData?.memorization?.dailyReviewDays || 60} days</strong></p>
            <p>• Then reviewed weekly for <strong className="text-foreground">{profileData?.memorization?.weeklyReviewWeeks || 6} weeks</strong></p>
            <p>• Then reviewed monthly for <strong className="text-foreground">{profileData?.memorization?.monthlyReviewMonths || 3} months</strong></p>
            <p>• New verses will be added every <strong className="text-foreground">{profileData?.memorization?.addToMemorizationIntervalValue || 1} {profileData?.memorization?.addToMemorizationInterval || 'weekly'}</strong></p>
          </div>
        </div>
      </div>
      {/* Data Management */}
      <div className="bg-card rounded-2xl border border-border shadow-warm-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
            <Database className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-bold text-foreground">Data Management</h2>
            <p className="text-muted-foreground">Export, import, and manage your data</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Data Export */}
          <div className="space-y-4">
            <h3 className="font-semibold text-foreground flex items-center space-x-2">
              <Download className="w-4 h-4 text-success" />
              <span>Export Your Data</span>
            </h3>
            <div className="bg-success/10 border border-success/20 rounded-lg p-4">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Export Format</label>
                  <select value={exportFormat} onChange={(e) => setExportFormat(e?.target?.value)}
                    className="block w-full px-3 py-2 border border-success/30 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-success text-sm bg-background">
                    <option value="json">JSON (Recommended)</option>
                    <option value="csv">CSV (Spreadsheet)</option>
                    <option value="xml">XML</option>
                  </select>
                </div>
                <button
                  onClick={handleDataExport}
                  disabled={exportStatus === 'loading'}
                  className="w-full flex items-center justify-center space-x-2 py-2 px-4 rounded-lg text-sm font-medium text-primary-foreground transition-colors bg-success hover:bg-success/90 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {exportStatus === 'loading' ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      <span>Exporting...</span>
                    </>
                  ) : exportStatus === 'success' ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      <span>Exported!</span>
                    </>
                  ) : exportStatus === 'error' ? (
                    <>
                      <XCircle className="w-4 h-4" />
                      <span>Export Failed</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      <span>Export Data</span>
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>• Includes all verses, reflections, and progress</p>
              <p>• Personal information and settings included</p>
              <p>• File will be downloaded to your device</p>
            </div>
          </div>

          {/* Data Import */}
          <div className="space-y-4">
            <h3 className="font-semibold text-foreground flex items-center space-x-2">
              <Upload className="w-4 h-4 text-primary" />
              <span>Import Data</span>
            </h3>
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Select File</label>
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleFileSelect}
                    className="block w-full text-sm text-foreground border border-border rounded-lg cursor-pointer bg-background focus:outline-none"
                  />
                </div>
                {importStatus === 'preview' && importPreview && (
                  <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 text-xs text-foreground space-y-1">
                    <p className="font-semibold">Preview:</p>
                    <p>• {importPreview?.verseCount} verse(s) to import</p>
                    <p>• {importPreview?.reflectionCount} reflection(s) to import</p>
                    {importPreview?.exportDate && (
                      <p>• Exported: {new Date(importPreview.exportDate)?.toLocaleDateString()}</p>
                    )}
                  </div>
                )}
                {importStatus === 'success' && (
                  <div className="flex items-center space-x-2 text-success text-xs">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{importMessage}</span>
                  </div>
                )}
                {importStatus === 'error' && (
                  <div className="flex items-center space-x-2 text-destructive text-xs">
                    <XCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{importMessage}</span>
                  </div>
                )}
                <button
                  onClick={handleDataImport}
                  disabled={importStatus !== 'preview'}
                  className={`w-full flex items-center justify-center space-x-2 py-2 px-4 rounded-lg text-sm font-medium text-primary-foreground transition-colors ${
                    importStatus === 'preview' ? 'bg-primary hover:bg-primary/90' : 'bg-muted text-muted-foreground cursor-not-allowed'
                  }`}
                >
                  {importStatus === 'loading' ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      <span>Importing...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      <span>Import Data</span>
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
                <div className="text-xs text-warning">
                  <p className="font-medium">Import Warning</p>
                  <p className="text-muted-foreground">Importing will merge with existing data. Backup current data first. Only JSON exports from Fortified Mind are supported.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* App Preferences */}
      <div className="bg-card rounded-2xl border border-border shadow-warm-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="h-10 w-10 bg-accent/10 rounded-full flex items-center justify-center">
            <Settings className="w-5 h-5 text-accent" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-bold text-foreground">App Preferences</h2>
            <p className="text-muted-foreground">Customize your app experience</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Theme Preference</label>
            <select
              value={preferences?.theme}
              onChange={(e) => handlePreferenceChange('theme', e?.target?.value)}
              disabled={!isEditing}
              className={selectClass(!isEditing)}
            >
              <option value="light">Light Mode</option>
              <option value="dark">Dark Mode</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">Changes apply immediately and are saved to your account</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Language</label>
            <select
              value={preferences?.language}
              onChange={(e) => handlePreferenceChange('language', e?.target?.value)}
              disabled={!isEditing}
              className={selectClass(!isEditing)}
            >
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="fr">Français</option>
              <option value="de">Deutsch</option>
              <option value="pt">Português</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">Saved to your account profile</p>
          </div>
        </div>
      </div>
      {/* System Information */}
      <div className="bg-muted/50 rounded-2xl border border-border shadow-warm p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="h-8 w-8 bg-muted rounded-full flex items-center justify-center">
            <Smartphone className="w-4 h-4 text-muted-foreground" />
          </div>
          <h3 className="font-semibold text-foreground">System Information</h3>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">App Version</p>
            <p className="font-medium text-foreground">{systemInfo?.appVersion}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Last Activity</p>
            <p className="font-medium text-foreground">{systemInfo?.lastSync}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Data Size (est.)</p>
            <p className="font-medium text-foreground">{systemInfo?.storageUsed}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Member Since</p>
            <p className="font-medium text-foreground">{systemInfo?.accountSince}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedSettings;